<template>
  <div>
    <merus-data-table
      :headers="headers"
      :items="agrupamentos"
      :loading="loading"
      :loading-text="$t('system.messages.loading')"
      :default-multi-sort="defaultSort"
      @update:displayedItems="displayedAgrupamentos = $event"
    >
      <template v-slot:toolbar>
        <cadastro-toolbar
          :title="$t('subsidyMotorCatalog.groupings.title')"
          :token="token"
          :items="displayedAgrupamentos !== null ? displayedAgrupamentos : agrupamentos"
          :csv-title="$t('subsidyMotorCatalog.groupings.listTitle')"
          :csv-headers="headers"
          hide-search
        >
          <template v-slot:add v-if="canInsert">
            <v-btn color="primary" dark small class="mb-2" fab @click="openNewAgrupamento">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
        </cadastro-toolbar>
      </template>
      <template v-slot:item.campos_chave_label="{ item }">
        <v-chip v-for="(campo, idx) in item.campos_chave" :key="idx" small class="mr-1 mb-1">{{ campo }}</v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon color="green" dark small @click="editAgrupamento(item)" :disabled="!canEdit">mdi-pencil</v-icon>
        <v-icon color="red" dark small @click="deleteAgrupamento(item)" :disabled="!canDelete">mdi-delete</v-icon>
      </template>
    </merus-data-table>

    <v-dialog v-model="dialogAgrupamento" max-width="800px" persistent>
      <v-card>
        <v-card-title>{{ dialogAgrupamentoTitle }}</v-card-title>
        <v-card-text>
          <v-form ref="formAgrupamento" v-model="formAgrupamentoValidado">
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="editedAgrupamento.codigo"
                  :label="$t('subsidyMotorCatalog.fields.code')"
                  :rules="[requiredRule]"
                  :disabled="editedAgrupamentoIndex !== -1"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field v-model="editedAgrupamento.descricao" :label="$t('system.description')" :rules="[requiredRule]" />
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span>{{ $t("subsidyMotorCatalog.fields.keyFields") }}</span>
                  <v-btn small color="primary" @click="addCampoChave">
                    <v-icon left small>mdi-plus</v-icon>
                    {{ $t("subsidyMotorCatalog.addKeyField") }}
                  </v-btn>
                </div>
                <v-row v-for="(campo, idx) in editedAgrupamento.campos_chave" :key="'campo-' + idx" dense>
                  <v-col cols="10">
                    <v-combobox
                      v-model="editedAgrupamento.campos_chave[idx]"
                      :items="campoChavePresets"
                      :label="$t('subsidyMotorCatalog.fields.keyField')"
                      :rules="[requiredRule]"
                      dense
                    />
                  </v-col>
                  <v-col cols="2" class="d-flex align-center">
                    <v-btn icon small color="red" @click="removeCampoChave(idx)" :disabled="editedAgrupamento.campos_chave.length <= 1">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" dark @click="saveAgrupamento">{{ $t("button.save") }}</v-btn>
          <v-btn text @click="closeAgrupamento">{{ $t("button.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MerusDataTable from "../../Tables/MerusDataTable.vue";
import CadastroToolbar from "../../Toolbars/CadastroToolbar.vue";
import apiController from "../../../utils/apiController";
import EventBus from "../../../utils/eventBus";

const emptyAgrupamento = () => ({
  id_agrupamento: null,
  codigo: "",
  descricao: "",
  campos_chave: ["dia_operacional"],
});

export default {
  components: {
    MerusDataTable,
    CadastroToolbar,
  },
  props: {
    agrupamentos: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    token: { type: String, default: null },
    canInsert: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    defaultSort: { type: Array, default: () => [] },
  },
  data: () => ({
    displayedAgrupamentos: null,
    dialogAgrupamento: false,
    formAgrupamentoValidado: true,
    editedAgrupamentoIndex: -1,
    editedAgrupamento: emptyAgrupamento(),
    campoChavePresets: [
      "hora_operacional",
      "dia_operacional",
      "ano_mes",
      "id_viagem_grid",
      "id_linha",
      "id_veiculo",
      "id_unidade",
    ],
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("subsidyMotorCatalog.fields.code"), value: "codigo", sortable: true, filtrable: true },
        { text: this.$t("system.description"), value: "descricao", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.keyFields"), value: "campos_chave_label", sortable: false, filtrable: false },
        { text: this.$t("contract.table.headers.actions"), value: "actions", sortable: false, filtrable: false },
      ];
    },
    dialogAgrupamentoTitle() {
      return this.editedAgrupamentoIndex === -1
        ? this.$t("subsidyMotorCatalog.groupings.new")
        : `${this.$t("button.edit")} — ${this.$t("subsidyMotorCatalog.tabs.groupings")}`;
    },
    requiredRule() {
      return (v) => (!!v && String(v).trim().length > 0) || this.$t("system.messages.requiredField");
    },
  },
  methods: {
    openNewAgrupamento() {
      this.editedAgrupamentoIndex = -1;
      this.editedAgrupamento = emptyAgrupamento();
      this.dialogAgrupamento = true;
    },
    editAgrupamento(item) {
      this.editedAgrupamentoIndex = this.agrupamentos.indexOf(item);
      this.editedAgrupamento = {
        id_agrupamento: item.id_agrupamento,
        codigo: item.codigo,
        descricao: item.descricao,
        campos_chave: Array.isArray(item.campos_chave) ? [...item.campos_chave] : [],
      };
      this.dialogAgrupamento = true;
    },
    addCampoChave() {
      this.editedAgrupamento.campos_chave.push("");
    },
    removeCampoChave(idx) {
      this.editedAgrupamento.campos_chave.splice(idx, 1);
    },
    closeAgrupamento() {
      this.dialogAgrupamento = false;
      this.editedAgrupamento = emptyAgrupamento();
      this.editedAgrupamentoIndex = -1;
      if (this.$refs.formAgrupamento) this.$refs.formAgrupamento.resetValidation();
    },
    async saveAgrupamento() {
      if (this.$refs.formAgrupamento) this.$refs.formAgrupamento.validate();
      if (!this.formAgrupamentoValidado) return;
      const endpoint =
        this.editedAgrupamentoIndex === -1
          ? apiController.endpoints.subsidioMotorInsertAgrupamento
          : apiController.endpoints.subsidioMotorUpdateAgrupamento;
      try {
        const resp = await apiController.request(endpoint, { data: this.editedAgrupamento, silent: true });
        if (resp.data && resp.data.erro) {
          EventBus.$emit("show-alert", { mensagem: resp.data.mensagem || this.$t("system.unknownSystemError"), tipo: "error" });
          return;
        }
        EventBus.$emit("show-alert", { mensagem: this.$t("subsidyMotorCatalog.saved"), tipo: "success" });
        this.closeAgrupamento();
        this.$emit("refresh", ["agrupamentos"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async deleteAgrupamento(item) {
      try {
        await apiController.request(apiController.endpoints.subsidioMotorDeleteAgrupamento, {
          data: { id_agrupamento: item.id_agrupamento },
          silent: true,
        });
        this.$emit("refresh", ["agrupamentos"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
  },
};
</script>
